import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';
import { omit } from 'lodash-es';
import { showError, request } from '@utils';
import { boardsServices } from '@services';
import { QUERY_KEYS } from 'constants/queryKeys';
import { useAssignees } from 'hooks/common';
import { useDispatch, useSelector } from 'react-redux';
import {
  filtersModalConditionSellRequestsChange,
  filtersModalRulesSellRequestsChange,
  selectSellRequestsState,
} from '@store';
import {
  getColumns,
  normalizeSellRequestsTasks,
  getSellRequestsQueryParamsAndPayload,
} from './utils';
import { SELL_REQUESTS_BOARD, SELL_REQUESTS_PAGE_SIZE } from './constants';

export const useSellRequestsOperations = () => {
  const router = useRouter();
  const { filters } = useSelector(selectSellRequestsState);
  const dispatch = useDispatch();
  const { assigneesWithNone, isLoading: isLoadingAssignees } = useAssignees();
  const page = router.query?.page || 1;
  const pageSize = router.query?.pageSize || SELL_REQUESTS_PAGE_SIZE;
  const orderBy = router.query?.orderBy || 'createdAt';
  const order = router.query?.order || 'desc';
  const query = router.query?.query;
  const archived = router.query?.archived;
  const assignee = router.query?.assignee;
  const board = SELL_REQUESTS_BOARD;

  const KNOWN_QUERY_PARAMS = [
    'page',
    'pageSize',
    'orderBy',
    'order',
    'query',
    'archived',
    'assignee',
    'view',
    'viewMode',
  ];

  const extraParams = useMemo(
    () => omit(router.query, KNOWN_QUERY_PARAMS),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router.query]
  );

  const params = useMemo(
    () => ({
      page,
      pageSize: parseInt(pageSize, 10) || SELL_REQUESTS_PAGE_SIZE,
      orderBy,
      order,
      query,
      archived,
      assignee,
      ...extraParams,
    }),
    [page, pageSize, orderBy, order, query, archived, assignee, extraParams]
  );

  const { cleanParams, payloadSellRequests, exportPayload } = useMemo(
    () => getSellRequestsQueryParamsAndPayload({ params, filters }),
    [params, filters]
  );

  const sellRequestsResponse = useQuery(
    [QUERY_KEYS.GET_SELL_REQUESTS_KEY, cleanParams, filters.modalFiltersRules],
    () =>
      boardsServices.getBoardTasks({
        boardId: board.id,
        params: cleanParams,
        payload: payloadSellRequests,
      })
  );

  const enums = useQuery(
    [QUERY_KEYS.GET_BOARDS_ENUMS, board.id],
    () => boardsServices.getEnumsPerBoardId(board.id),
    { enabled: router.isReady }
  );

  const handleChangeRules = (rules) => {
    dispatch(filtersModalRulesSellRequestsChange(rules));
  };

  const handleFiltersModalChange = (conditions) => {
    dispatch(filtersModalConditionSellRequestsChange(conditions));
  };

  const sellRequestsTasks = normalizeSellRequestsTasks(
    sellRequestsResponse?.data?.data || []
  );

  const statuses = board.statusOrder.reduce((acc, cur) => {
    acc.push({
      text: board.statuses[cur].label,
      value: Number(cur),
    });
    return acc;
  }, []);

  const _addTask = async (payload = {}) => {
    const data = {
      body: payload,
    };
    const response = await request.post(`/v1/www/vehicle-sales`, data);

    if (response?.error) {
      showError(response?.message);
    }
    await sellRequestsResponse.refetch();
  };

  const updateTask = async (payload = {}) => {
    try {
      if (!payload?.id) return;

      const taskId = payload?.id;
      await boardsServices.updateTask(taskId, payload);
    } catch (error) {
      showError(error);
    }
  };

  useEffect(() => {
    if (sellRequestsResponse.isError) {
      showError(sellRequestsResponse?.error);
    }
  }, [sellRequestsResponse.isError]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (enums.isError) {
      showError(enums?.error);
    }
  }, [enums.isError]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    page,
    pageSize: parseInt(pageSize, 10) || SELL_REQUESTS_PAGE_SIZE,
    total: sellRequestsResponse?.data?.meta?.total,
    loading:
      sellRequestsResponse?.isLoading ||
      sellRequestsResponse?.isFetching ||
      isLoadingAssignees,
    error: sellRequestsResponse?.error,
    isSuccess: sellRequestsResponse?.isSuccess,
    sellRequests: sellRequestsTasks?.tasks,
    totalFinalPrice: sellRequestsTasks?.totalFinalPrice,
    board,
    assignees: assigneesWithNone,
    statuses,
    columns: getColumns(sellRequestsTasks?.tasks || [], board),
    enums,
    sellRequestsParams: cleanParams,
    sellRequestsQueryPayload: exportPayload,
    refresh: sellRequestsResponse.refetch,
    addTask: _addTask,
    updateTask,
    handleChangeRules,
    handleFiltersModalChange,
    filters,
  };
};
